// Set the timeout period (in minutes)
import router from '../../router/index'
import authStorageService from '../../services/auth/auth-storage.service.js'

const TIMEOUT_PERIOD = Number(process.env.VUE_APP_SESSION_TIMEOUT_MINUTES) || 30; // in minutes
const WARNING_PERIOD = Number(process.env.VUE_APP_SESSION_WARNING_SECONDS) || 360; // in seconds
let sessionTimer = null;
let warningTimer = null;
let isInitialized = false;

// Function to show session warning toast
function showSessionWarning() {
  // When warning appears after 10 seconds of inactivity,
  // the user has (total session time - 10 seconds) remaining until expiry
  const totalSessionTimeSeconds = TIMEOUT_PERIOD * 60; // 300 seconds
  const timeRemainingUntilExpiry = totalSessionTimeSeconds - WARNING_PERIOD; // 300 - 10 = 290 seconds (4min 50sec)
  
  const event = new CustomEvent('sessionWarning', {
    detail: { 
      warningTime: WARNING_PERIOD, // When warning appears (10s)
      timeRemainingUntilExpiry: timeRemainingUntilExpiry, // Time left: 290 seconds
      totalSessionTime: totalSessionTimeSeconds, // Total: 300 seconds
      timestamp: Date.now()
    }
  });
  window.dispatchEvent(event);
}

// Function to handle session timeout
function handleSessionTimeout() {
  // Clear all authentication data
  authStorageService.clearAuthData();
  
  // Show session expired message instead of just redirecting
  const event = new CustomEvent('sessionExpired', {
    detail: { 
      message: 'Your session has expired. Please sign in again.',
      timestamp: Date.now()
    }
  });
  window.dispatchEvent(event);
  
  // Redirect to login page
  router.push({ name: 'login' }).catch(err => {
    console.warn('Navigation to login failed:', err);
    if (typeof window !== 'undefined') {
      window.location.replace(`${window.location.origin}/#/login`);
    }
  });
}

// Function to start/restart the session timer
function startSessionTimer(logConfig = false) {
  // Clear existing timers
  if (sessionTimer) {
    clearTimeout(sessionTimer);
  }
  if (warningTimer) {
    clearTimeout(warningTimer);
  }
  
  const totalSessionTime = TIMEOUT_PERIOD * 60 * 1000; // minutes to ms (5 min = 300,000ms)
  const inactivityThreshold = WARNING_PERIOD * 1000; // seconds to ms (10s = 10,000ms)
  
  // Only log configuration when explicitly requested (e.g., on initial start)
  if (logConfig) {
    console.log(`Session config: Total time: ${TIMEOUT_PERIOD} minutes, Warning after: ${WARNING_PERIOD} seconds of inactivity`);
  }
  
  // Set warning timer (shows warning after specified seconds of inactivity)
  warningTimer = setTimeout(() => {
    console.log('Showing session warning after inactivity');
    showSessionWarning();
  }, inactivityThreshold); // Warning after 10 seconds
  
  // Set session expiry timer  
  sessionTimer = setTimeout(() => {
    console.log('Session expired');
    handleSessionTimeout();
  }, totalSessionTime); // Session expires after 5 minutes
}

// Initialize session management only once
if (!isInitialized) {
  // Only add event listeners, don't start timer yet
  // Timer will start after successful login via resetSessionTimer
  document.addEventListener('mousemove', resetSessionTimer);
  document.addEventListener('keydown', resetSessionTimer);
  document.addEventListener('click', resetSessionTimer);
  document.addEventListener('scroll', resetSessionTimer);
  
  isInitialized = true;
}

// Reset the session timer function
export default function resetSessionTimer() {
  // Only reset if user is authenticated
  const user = authStorageService.getUser();
  if (user) {
    startSessionTimer(); // Don't log config on resets
  }
}

// Export function to start session timer (called after login)
export function startSession() {
  startSessionTimer(true); // Log config on initial session start
}

// Export function to manually clear session
export function clearSession() {
  if (sessionTimer) {
    clearTimeout(sessionTimer);
    sessionTimer = null;
  }
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
  authStorageService.clearAuthData();
}

// Export function to extend session (called from toast)
export function extendSession() {
  // Only extend if user is authenticated
  const user = authStorageService.getUser();
  if (user) {
    startSessionTimer(); // Don't log config on extensions
    // Dispatch event to hide warning toast
    const event = new CustomEvent('sessionExtended', {
      detail: { 
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }
}

// Export function to check if session is active
export function isSessionActive() {
  return authStorageService.getUser() !== null;
}
